import React, { useState } from 'react';
import type { TaxonomyClassification } from '../../../types/signals-live';
import SignalTag from '../shared/SignalTag';

interface Props {
  classification?: TaxonomyClassification;
}

const ConductIntelligencePanel: React.FC<Props> = ({ classification }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!classification?.dimensions) {
    return null;
  }

  // Get dimensions that have detected labels
  const dimensionsWithLabels = Object.entries(classification.dimensions).filter(
    ([_, dimension]) => {
      return Object.values(dimension.categories).some(category => 
        Object.keys(category.labels || {}).length > 0
      );
    }
  );

  if (dimensionsWithLabels.length === 0) {
    return null;
  }

  // Count total labels detected
  const totalLabelsCount = dimensionsWithLabels.reduce((total, [_, dimension]) => {
    return total + Object.values(dimension.categories).reduce((catTotal, category) => {
      return catTotal + Object.keys(category.labels || {}).length;
    }, 0);
  }, 0);

  // Define gradient colors for each dimension
  const getDimensionGradient = (dimensionId: string) => {
    const gradients: { [key: string]: string } = {
      dissatisfaction: 'from-purple to-indigo',
      customer_strain: 'from-[#D4D44F] to-[#E5E54F]',
      foreseeable_harm: 'from-[#FF4343] to-[#FF6B6B]',
      outcomes_and_cross_cutting: 'from-teal to-sky',
    };
    return gradients[dimensionId] || 'from-gray-400 to-gray-500';
  };

  // Get tag variant based on dimension
  const getTagVariant = (dimensionId: string) => {
    const variants: { [key: string]: string } = {
      dissatisfaction: 'complaint',
      customer_strain: 'strain',
      foreseeable_harm: 'harm',
      outcomes_and_cross_cutting: 'primary',
    };
    return variants[dimensionId] || 'default';
  };

  return (
    <div className="mb-6">
      <div className="bg-vanilla rounded-lg overflow-hidden">
        {/* Collapsible Header with Gradient */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full p-4 bg-gradient-to-r from-purple via-indigo to-teal text-white hover:opacity-90 transition-opacity"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <h2 className="text-figma-lg font-semibold">Conduct Intelligence Framework</h2>
              <span className="px-2 py-0.5 bg-white/20 rounded-full text-sm">
                {totalLabelsCount} {totalLabelsCount === 1 ? 'label' : 'labels'} detected
              </span>
              {classification?.taxonomy_version && (
                <span className="px-2 py-0.5 bg-white/10 rounded-full text-[11px] tracking-wide">
                  Taxonomy v{classification.taxonomy_version}
                </span>
              )}
            </div>
            <svg 
              className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
              fill="none" 
              viewBox="0 0 24 24"
            >
              <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
            </svg>
          </div>
        </button>
        
        {/* Expandable Content */}
        {isExpanded && (
          <div className="p-6">
            <div className="space-y-6">
          {dimensionsWithLabels.map(([dimensionId, dimension]) => (
            <div key={dimensionId}>
              {/* Dimension Header */}
              <div className={`inline-block px-3 py-1 rounded-full bg-gradient-to-r ${getDimensionGradient(dimensionId)} text-white text-sm font-semibold mb-3`}>
                {dimension.dimension_name}
              </div>
              
              {/* Categories and Labels */}
              <div className="space-y-4">
                {Object.entries(dimension.categories).map(([categoryId, category]) => {
                  const labels = Object.entries(category.labels || {});
                  if (labels.length === 0) return null;
                  
                  return (
                    <div key={categoryId}>
                      <div className="text-sm font-medium text-gray-600 mb-2">
                        {category.category_name}
                      </div>
                      
                      {/* Labels */}
                      <div className="space-y-3">
                        {labels.map(([labelId, label]) => (
                          <div key={labelId} className="bg-white rounded-lg p-3 border border-gray-100">
                            <div className="flex items-start justify-between mb-2">
                              <SignalTag 
                                label={label.label_name} 
                                variant={getTagVariant(dimensionId) as any}
                              />
                              <span className="text-sm font-semibold text-gray-600">
                                {Math.round(label.confidence * 100)}%
                              </span>
                            </div>
                            
                            <p className="text-sm text-gray-700 mb-2">
                              {label.rationale}
                            </p>
                            
                            {label.regulation_reference && (
                              <p className="text-xs text-gray-500">
                                <span className="font-medium">Regulation:</span> {label.regulation_reference}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          
          {/* Summary Tags */}
          {classification.summary_tags && classification.summary_tags.length > 0 && (
            <div className="pt-4 border-t border-gray-200">
              <div className="text-sm font-medium text-gray-600 mb-2">Summary Tags</div>
              <div className="flex flex-wrap gap-2">
                {classification.summary_tags.map((tag, index) => (
                  <span 
                    key={index}
                    className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConductIntelligencePanel;
